import { useCallback, useEffect, useState } from 'react';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Shield, ArrowClockwise, Trash, Warning, UserCircle, Clock, Lock, LockOpen, CheckCircle, MapPin, Globe } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

function fmtTime(s) {
  if (!s) return '—';
  try { return new Date(s).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', second: '2-digit' }); }
  catch { return s; }
}

function badge(code) {
  if (code === 403) return <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-red-100 text-red-700">403 FORBIDDEN</span>;
  if (code === 401) return <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-700">401 UNAUTH</span>;
  return <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-gray-100 text-gray-700">{code}</span>;
}

function roleBadge(role) {
  if (!role || role === '—') return <span className="text-gray-400 text-xs">anonymous</span>;
  const colors = {
    admin: 'bg-emerald-100 text-emerald-700',
    owner: 'bg-emerald-100 text-emerald-700',
    assistant: 'bg-blue-100 text-blue-700',
    accountant: 'bg-cyan-100 text-cyan-700',
    staff: 'bg-indigo-100 text-indigo-700',
    sub_staff: 'bg-indigo-50 text-indigo-600',
    distributor: 'bg-purple-100 text-purple-700',
    sales_person: 'bg-pink-100 text-pink-700',
    card_vendor: 'bg-orange-100 text-orange-700',
    gift_card_vendor: 'bg-fuchsia-100 text-fuchsia-700',
    recharge_vendor: 'bg-sky-100 text-sky-700',
    electricity_vendor: 'bg-yellow-100 text-yellow-700',
  };
  return <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${colors[role] || 'bg-gray-100 text-gray-700'}`}>{role}</span>;
}

export default function SecurityAuditPage() {
  const { user } = useAuth();
  const isOwner = user?.role === 'admin' || user?.role === 'owner';
  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [blockedUsers, setBlockedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hours, setHours] = useState(24);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [filterEmail, setFilterEmail] = useState('');
  const [filterPath, setFilterPath] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams();
      q.set('hours', String(hours));
      q.set('limit', '300');
      if (filterStatus) q.set('status_code', filterStatus);
      if (filterRole) q.set('role', filterRole);
      if (filterEmail) q.set('user_email', filterEmail);
      if (filterPath) q.set('path_contains', filterPath);
      const [s, l, a, b] = await Promise.all([
        api.get(`/security-audit/stats?hours=${hours}`),
        api.get(`/security-audit/logs?${q.toString()}`),
        api.get('/security-audit/alerts?only_active=true'),
        api.get('/security-audit/blocked-users'),
      ]);
      setStats(s.data);
      setLogs(l.data || []);
      setAlerts(a.data || []);
      setBlockedUsers(b.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Load failed');
    } finally { setLoading(false); }
  }, [hours, filterStatus, filterRole, filterEmail, filterPath]);

  useEffect(() => { load(); }, [load]);

  const clearOld = async () => {
    if (!window.confirm('Clear logs older than 7 days?')) return;
    try {
      const r = await api.delete('/security-audit/clear?older_than_hours=168');
      toast.success(`${r.data.deleted} logs deleted`);
      await load();
    } catch { toast.error('Clear failed'); }
  };

  const blockUser = async (user) => {
    if (!user?.user_id) { toast.error('Cannot block anonymous user'); return; }
    const mins = window.prompt(`Block ${user.name || user.email} for how many minutes?\n(1 = 1 min, 60 = 1 hr, 1440 = 1 day)`, '60');
    if (!mins) return;
    const m = Number(mins);
    if (!m || m < 1) return;
    const reason = window.prompt('Reason (optional):', 'Suspicious activity');
    try {
      await api.post(`/security-audit/users/${user.user_id}/block`, { minutes: m, reason });
      toast.success(`${user.email} blocked for ${m} min`);
      await load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Block failed'); }
  };

  const unblockUser = async (userId, email) => {
    if (!window.confirm(`Unblock ${email}?`)) return;
    try {
      await api.post(`/security-audit/users/${userId}/unblock`);
      toast.success(`${email} unblocked`);
      await load();
    } catch (err) { toast.error('Unblock failed'); }
  };

  const ackAlert = async (alertId) => {
    try {
      await api.post(`/security-audit/alerts/${alertId}/acknowledge`);
      toast.success('Alert acknowledged');
      await load();
    } catch { toast.error('Failed'); }
  };

  const [historyModal, setHistoryModal] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  if (!isOwner) {
    return (
      <div className="p-6 text-center text-red-600 font-medium">
        Access denied. Security Audit page sirf Owner/Admin ke liye hai.
      </div>
    );
  }

  const openHistory = async (userId, label) => {
    setHistoryModal({ userId, label });
    setHistoryLoading(true);
    setHistory([]);
    try {
      const r = await api.get(`/security-audit/users/${userId}/login-history?limit=20`);
      setHistory(r.data || []);
    } catch { toast.error('Failed to load history'); }
    finally { setHistoryLoading(false); }
  };

  return (
    <div className="space-y-5" data-testid="security-audit-page">
      <div className="flex items-center gap-2 flex-wrap">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Shield size={26} weight="duotone" className="text-red-600" />
          Security Audit Dashboard
        </h1>
        <div className="ml-auto flex gap-2 flex-wrap items-center">
          <select className="h-8 border rounded text-xs px-2" value={hours} onChange={(e) => setHours(Number(e.target.value))} data-testid="time-window">
            <option value={1}>Last 1 hour</option>
            <option value={6}>Last 6 hours</option>
            <option value={24}>Last 24 hours</option>
            <option value={72}>Last 3 days</option>
            <option value={168}>Last 7 days</option>
            <option value={720}>Last 30 days</option>
          </select>
          <Button size="sm" variant="outline" onClick={load} className="gap-1"><ArrowClockwise size={14} /> Refresh</Button>
          <Button size="sm" onClick={clearOld} variant="outline" className="text-red-600 border-red-200 hover:bg-red-50 gap-1"><Trash size={14} /> Clear &gt;7d</Button>
        </div>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <div className="text-[11px] uppercase text-red-700 font-semibold flex items-center gap-1"><Warning size={12} /> 403 Forbidden</div>
          <div className="text-3xl font-bold text-red-900">{stats?.forbidden_403 ?? '—'}</div>
          <div className="text-[10px] text-red-600">Unauthorized endpoint attempts</div>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
          <div className="text-[11px] uppercase text-amber-700 font-semibold">401 Unauth</div>
          <div className="text-3xl font-bold text-amber-900">{stats?.unauthorized_401 ?? '—'}</div>
          <div className="text-[10px] text-amber-600">Missing/expired session</div>
        </div>
        <div className="bg-gray-100 border border-gray-200 rounded-lg p-3">
          <div className="text-[11px] uppercase text-gray-700 font-semibold">Total Events</div>
          <div className="text-3xl font-bold text-gray-900">{stats?.total ?? '—'}</div>
          <div className="text-[10px] text-gray-600">Last {hours}h</div>
        </div>
        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3">
          <div className="text-[11px] uppercase text-indigo-700 font-semibold">Events / hr</div>
          <div className="text-3xl font-bold text-indigo-900">{stats?.total ? (stats.total / hours).toFixed(1) : '—'}</div>
          <div className="text-[10px] text-indigo-600">Avg frequency</div>
        </div>
      </div>

      {/* Active Brute-Force Alerts */}
      {alerts.length > 0 && (
        <div className="bg-gradient-to-r from-red-50 to-orange-50 border-2 border-red-300 rounded-xl p-4 space-y-2" data-testid="active-alerts">
          <div className="font-semibold text-red-900 flex items-center gap-2 text-sm">
            <Warning size={18} weight="fill" /> {alerts.length} active brute-force alert{alerts.length > 1 ? 's' : ''}
          </div>
          {alerts.map((a) => (
            <div key={a.id} className="bg-white border border-red-200 rounded-lg p-3 flex items-center justify-between flex-wrap gap-2">
              <div className="text-xs">
                <div className="font-semibold text-red-900 flex items-center gap-2 flex-wrap">
                  {a.user_name} ({a.user_email}) — {a.user_role}
                  {a.is_unusual_location && (
                    <span className="px-2 py-0.5 rounded bg-amber-100 text-amber-800 text-[10px] font-bold flex items-center gap-1">
                      <Warning size={10} weight="fill" /> UNUSUAL LOCATION
                    </span>
                  )}
                </div>
                <div className="text-gray-600 flex items-center gap-2 flex-wrap">
                  {a.count} forbidden attempts in {a.window_minutes} min
                  <span className="inline-flex items-center gap-1"><Globe size={11} /> {a.ip}</span>
                  <span className="inline-flex items-center gap-1"><MapPin size={11} /> {a.location_text || '—'}</span>
                  <span>{fmtTime(a.created_at)}</span>
                </div>
                <div className="text-[10px] text-gray-500 mt-0.5">Paths: {(a.paths || []).slice(0, 5).join(', ')}</div>
              </div>
              <div className="flex gap-1">
                <Button size="sm" variant="outline" onClick={() => openHistory(a.user_id, `${a.user_name} (${a.user_email})`)} className="h-7 text-xs gap-1" data-testid={`alert-history-${a.id}`}>
                  <Clock size={12} /> Login History
                </Button>
                <Button size="sm" onClick={() => blockUser({ user_id: a.user_id, email: a.user_email, name: a.user_name })} className="bg-red-600 hover:bg-red-700 h-7 text-xs gap-1" data-testid={`alert-block-${a.id}`}>
                  <Lock size={12} /> Block
                </Button>
                <Button size="sm" variant="outline" onClick={() => ackAlert(a.id)} className="h-7 text-xs gap-1">
                  <CheckCircle size={12} /> Ack
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Currently Blocked Users */}
      {blockedUsers.length > 0 && (
        <div className="bg-gray-50 border border-gray-300 rounded-xl p-4" data-testid="blocked-users">
          <div className="font-semibold text-gray-900 flex items-center gap-2 text-sm mb-2">
            <Lock size={16} /> Currently Blocked Users ({blockedUsers.length})
          </div>
          <div className="space-y-2">
            {blockedUsers.map((u) => (
              <div key={u.id} className="bg-white border rounded-lg p-2 flex items-center justify-between flex-wrap gap-2 text-xs">
                <div>
                  <b>{u.name}</b> <span className="text-gray-500">({u.email})</span> {roleBadge(u.role)}
                  <div className="text-[10px] text-gray-500">Until {fmtTime(u.blocked_until)} • Reason: {u.block_reason} • By {u.blocked_by_name}</div>
                </div>
                <Button size="sm" variant="outline" onClick={() => unblockUser(u.id, u.email)} className="h-7 text-xs gap-1 border-emerald-400 text-emerald-700 hover:bg-emerald-50" data-testid={`unblock-${u.id}`}>
                  <LockOpen size={12} /> Unblock
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Top offenders + Top paths */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white border rounded-xl overflow-hidden">
          <div className="bg-red-50 px-4 py-2 border-b text-sm font-semibold text-red-900 flex items-center gap-2">
            <UserCircle size={16} /> Top Offenders (by email)
          </div>
          <div className="max-h-64 overflow-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-[10px] uppercase text-gray-500 sticky top-0">
                <tr>
                  <th className="px-3 py-1.5 text-left">User</th>
                  <th className="px-3 py-1.5 text-left">Role</th>
                  <th className="px-3 py-1.5 text-right">Attempts</th>
                  <th className="px-3 py-1.5 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {(stats?.top_users || []).map((u, i) => (
                  <tr key={u.user_id || u.email || `u-${i}`} className="border-t">
                    <td className="px-3 py-1.5">
                      <div className="text-xs font-medium">{u.name}</div>
                      <div className="text-[10px] text-gray-500">{u.email}</div>
                    </td>
                    <td className="px-3 py-1.5">{roleBadge(u.role)}</td>
                    <td className="px-3 py-1.5 text-right font-bold">{u.count}</td>
                    <td className="px-3 py-1.5 text-right">
                      {u.user_id && !['owner', 'admin', '—'].includes(u.role) ? (
                        <Button size="sm" variant="outline" onClick={() => blockUser(u)} className="h-6 text-[10px] px-2 gap-1 border-red-300 text-red-700 hover:bg-red-50" data-testid={`offender-block-${i}`}>
                          <Lock size={10} /> Block
                        </Button>
                      ) : <span className="text-[10px] text-gray-400">—</span>}
                    </td>
                  </tr>
                ))}
                {(!stats?.top_users || stats.top_users.length === 0) && (
                  <tr><td colSpan="4" className="px-3 py-4 text-center text-xs text-gray-400">No events</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-white border rounded-xl overflow-hidden">
          <div className="bg-indigo-50 px-4 py-2 border-b text-sm font-semibold text-indigo-900 flex items-center gap-2">
            Top Attempted Paths
          </div>
          <div className="max-h-64 overflow-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-[10px] uppercase text-gray-500 sticky top-0">
                <tr>
                  <th className="px-3 py-1.5 text-left">Path</th>
                  <th className="px-3 py-1.5 text-right">Attempts</th>
                </tr>
              </thead>
              <tbody>
                {(stats?.top_paths || []).map((p, i) => (
                  <tr key={p.path || `p-${i}`} className="border-t">
                    <td className="px-3 py-1.5 font-mono text-xs">{p.path}</td>
                    <td className="px-3 py-1.5 text-right font-bold">{p.count}</td>
                  </tr>
                ))}
                {(!stats?.top_paths || stats.top_paths.length === 0) && (
                  <tr><td colSpan="2" className="px-3 py-4 text-center text-xs text-gray-400">No events</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Filter row */}
      <div className="bg-white border rounded-lg p-3 flex gap-2 flex-wrap items-center">
        <select className="h-8 text-xs border rounded px-2" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} data-testid="filter-status">
          <option value="">All status</option>
          <option value="403">403 only</option>
          <option value="401">401 only</option>
        </select>
        <select className="h-8 text-xs border rounded px-2" value={filterRole} onChange={(e) => setFilterRole(e.target.value)} data-testid="filter-role">
          <option value="">All roles</option>
          <option value="card_vendor">Card Vendor</option>
          <option value="gift_card_vendor">Gift Card Vendor</option>
          <option value="recharge_vendor">Recharge Vendor</option>
          <option value="electricity_vendor">Electricity Vendor</option>
          <option value="sub_staff">Sub Staff</option>
          <option value="staff">Staff</option>
          <option value="distributor">Distributor</option>
          <option value="sales_person">Sales Person</option>
          <option value="accountant">CA</option>
        </select>
        <Input className="h-8 text-xs max-w-xs" placeholder="Email contains..." value={filterEmail} onChange={(e) => setFilterEmail(e.target.value)} data-testid="filter-email" />
        <Input className="h-8 text-xs max-w-xs" placeholder="Path contains..." value={filterPath} onChange={(e) => setFilterPath(e.target.value)} data-testid="filter-path" />
      </div>

      {/* Logs table */}
      <div className="bg-white border rounded-xl overflow-hidden">
        <div className="px-4 py-2 bg-gray-50 border-b text-xs text-gray-600">
          Showing <b>{logs.length}</b> events (latest first)
        </div>
        {loading ? <div className="p-8 text-center text-sm text-gray-500">Loading...</div>
        : logs.length === 0 ? <div className="p-8 text-center text-sm text-gray-500">No events in this window</div>
        : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-[10px] uppercase text-gray-500 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left">When</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-left">User</th>
                  <th className="px-3 py-2 text-left">Role</th>
                  <th className="px-3 py-2 text-left">Method</th>
                  <th className="px-3 py-2 text-left">Path</th>
                  <th className="px-3 py-2 text-left">IP</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((l) => (
                  <tr key={l.id} className="border-t hover:bg-gray-50">
                    <td className="px-3 py-1.5 text-[11px] text-gray-600 whitespace-nowrap"><Clock size={10} className="inline mr-1" />{fmtTime(l.timestamp)}</td>
                    <td className="px-3 py-1.5">{badge(l.status_code)}</td>
                    <td className="px-3 py-1.5">
                      <div className="text-xs font-medium">{l.user_name}</div>
                      <div className="text-[10px] text-gray-500">{l.user_email}</div>
                    </td>
                    <td className="px-3 py-1.5">{roleBadge(l.user_role)}</td>
                    <td className="px-3 py-1.5 text-[10px] font-mono">{l.method}</td>
                    <td className="px-3 py-1.5 font-mono text-[11px]">{l.path}{l.query ? `?${l.query.slice(0, 40)}` : ''}</td>
                    <td className="px-3 py-1.5 font-mono text-[10px] text-gray-500">{l.ip}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Login History Modal */}
      {historyModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setHistoryModal(null)}>
          <div className="bg-white rounded-xl max-w-3xl w-full max-h-[80vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="px-4 py-3 bg-indigo-50 border-b flex items-center justify-between">
              <div>
                <div className="font-semibold text-indigo-900 flex items-center gap-2"><Clock size={16} /> Recent Login History</div>
                <div className="text-xs text-indigo-700">{historyModal.label}</div>
              </div>
              <button onClick={() => setHistoryModal(null)} className="text-gray-500 hover:text-gray-700 text-xl">×</button>
            </div>
            <div className="overflow-auto p-4">
              {historyLoading ? <div className="text-center text-sm text-gray-500 py-8">Loading...</div>
              : history.length === 0 ? <div className="text-center text-sm text-gray-500 py-8">No login history found</div>
              : (
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-[10px] uppercase text-gray-500">
                    <tr>
                      <th className="px-3 py-1.5 text-left">When</th>
                      <th className="px-3 py-1.5 text-left">IP</th>
                      <th className="px-3 py-1.5 text-left">Location</th>
                      <th className="px-3 py-1.5 text-left">ISP</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((h, i) => (
                      <tr key={h.login_at || `h-${i}`} className="border-t">
                        <td className="px-3 py-1.5 text-xs">{fmtTime(h.login_at)}</td>
                        <td className="px-3 py-1.5 font-mono text-[11px]">{h.ip || '—'}</td>
                        <td className="px-3 py-1.5 text-xs"><MapPin size={10} className="inline mr-1" />{h.location_text}</td>
                        <td className="px-3 py-1.5 text-[10px] text-gray-500">{(h.geo?.isp) || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
